<template>
  <teleport to="body">
    <transition name="slide-fade" mode="out-in">
      <div v-if="showChat && book" class="fixed z-9999 top-0 h-full w-full">
        <div v-clickOutside="closeChat" class="fixed top-0 right-0 h-full w-full max-w-650 md:w-2/3
          lg:w-3/7 bg-primary shadow-dropdown flex flex-col"
        >
          <!-- H E A D E R -->
          <div class="relative pt-15 md:pt-20 pb-15 px-15 lg:pl-60 md:pl-20">
            <ButtonModule
              prefix="icon-close"
              :has-hover="true"
              class="absolute right-14 md:right-25 top-20 text-14 md:text-18 text-grey-275 cursor-pointer"
              @click="closeChat"
            />
            <!-- title -->
            <div class="flex justify-between">
              <h1 class="font-bold text-25 md:text-37 text-app-color leading-normal">Chat</h1>
            </div>
            <div class="flex justify-between">
              <div
                class="relative text-14 md:text-16 flex justify-between w-full"
                :class="isDarkMode ? 'text-app-color' : 'text-dark-500'"
              >
                <div class="flex items-center md:w-full max-w-70% flex-grow-0 relative -mt-6">
                  <p class="opacity-50 truncate w-auto mr-5">{{ book.title }}</p>
                  <span v-if="!!bookMembers && currentBook" class="opacity-50 pl-10 pr-5">•</span>
                  <SharedWith
                    v-if="!!bookMembers && currentBook"
                    class="flex-shrink-0"
                    classes="opacity-50"
                    :members="shareWith"
                    :book="currentBook"
                    :allow-delete="false"
                    width="w-225"
                  >
                    <p class="cursor-pointer">
                      {{ bookMembers.length }} member<span v-if="bookMembers.length > 1">s</span>
                    </p>
                  </SharedWith>
                </div>
                <div class="flex items-center md:mr-15">
                  <!-- search button -->
                  <i class="icon-search cursor-pointer text-19 transition-all duration-300 opacity-50 hover:opacity-100"
                     :class="[isOpenSearch ? 'opacity-100' : isDarkMode ? 'text-app-color opacity-50 hover:opacity-100' : 'text-dark-500 opacity-50 hover:opacity-100']"
                     @click.stop="openSearch"
                  />
                  <SetNotifications
                    v-if="false"
                    title="Turn off notifications"
                    :muted-at="chatMutedAt"
                    @setNotification="updateMutedAt"
                  />
                  <!-- more button -->
                  <i v-if="false" class="icon-more-horizontal cursor-pointer text-21 transition-all duration-300 opacity-50 hover:opacity-100"
                     :class="isDarkMode ? 'text-app-color' : 'text-dark-500'"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- <TransitionExpand> -->
          <div
            v-if="isOpenSearch"
            v-clickOutside="() => isOpenSearch = false"
            class="w-full flex items-center bg-book pl-10 md:pl-60 pr-10"
            @click="toggleInput"
          >
            <i
              class="icon-search text-app-color cursor-pointer opacity-100 text-20"
            />
            <input
              ref="inputSearchRef"
              v-model="searchValue"
              type="text"
              placeholder="Search this chat..."
              class="bg-book w-full p-15 text-14 md:text-16 md:p-15 outline-none"
            >
          </div>
          <!-- D I V I D E R -->
          <div class="border-b" :class="isDarkMode ? 'opacity-50 border-dark-500' : 'border-grey-400'" />
          <!-- </TransitionExpand> -->
          <!-- C H A T  L I S T-->
          <div ref="scrollToDownRef" class="px-15 md:px-20 lg:px-60 flex flex-col h-full overflow-y-auto pt-30" @scroll="scrollListener">
            <!-- C A R D -->
            <ChatMessage
              v-for="(message, index) in filteredMessages"
              :ref="el => messageRefs[message.id] = el"
              :key="message.id"
              :message="message"
              class="max-w-6/7 md:max-w-3/5 pb-20 text-14 md:text-15"
              :class="[user.id === message.userId ? 'self-end' : 'self-start', {'mt-auto': index === 0}]"
              :classes="user.id === message.userId ? 'justify-end' : 'justify-start'"
              :highlighted="highlightedMessage == message.id"
            />
          </div>

          <!-- E D I T O R -->
          <ChatEditor
            ref="chatEditorRef"
            class="mx-0 md:mx-20 lg:mx-60"
            :chat-id="book.chat.id"
            :mentions="book.projects"
            :book-id="book.id"
            :projects="book.projects"
            :share-with="shareWith"
            @message="pushMessage"
            @scroll-to-down="scrollToElement"
          />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, ref, computed, nextTick, watch, onBeforeUnmount, onMounted } from 'vue'
import { useStore } from 'vuex'

import { chatService } from '@/services'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import SetNotifications from '@/components/shared/SetNotifications'
import ChatEditor from '@/components/chat/ChatEditor'
import ChatMessage from '@/components/chat/ChatMessage'
import SharedWith from '@/components/books/SharedWith'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { ButtonModule, ChatEditor, ChatMessage, SetNotifications, SharedWith },

  props: {
    showChat: { type: Boolean }
  },

  emits: ['closeChat'],

  setup (props, { emit }) {
    const { state, getters, dispatch, commit } = useStore()
    const route = useRoute()

    const chatEditorRef = ref(null)
    const messageRefs = ref({})

    const book = computed(() => state.book.book)
    const books = computed(() => state.book.books)
    const user = computed(() => state.profile.user)
    const token = computed(() => state.auth.token)
    const bookMembers = computed(() => state.book.bookMembers)
    const highlightedMessage = computed(() => route.query.message)

    dispatch('getMembers', { limit: 100 })

    const currentBook = computed(() => books.value.find(b => b.id === book.value.id) || state.team.headquarters)

    const shareWith = computed(() => currentBook.value.shareWith || state.team.members)

    let ws = null
    let needReconnection = true

    const init = () => {
      if (ws) {
        ws.onopen = ws.onopen = ws.onerror = null
        ws.close()
      }

      ws = new WebSocket(`${process.env.VUE_APP_WEBSOCKET}?auth=${token.value}`)

      ws.onmessage = ({ data }) => {
        const value = JSON.parse(data)

        if (value.name === 'chat' && value.payload?.message) {
          if (value.payload.message.chatId === book.value.chat.id && value.payload.message.user.id !== user.value.id) {
            commit('setMessage', value.payload.message)
            scrollToElement()
          }
          if (props.showChat) {
            dispatch('updateMessageStatus', { chatId: book.value.chat.id })
          }
        }
      }

      ws.onclose = () => {
        ws = null
        if (needReconnection) {
          setTimeout(() => init(), 1000)
        }
      }
    }

    init()

    onBeforeUnmount(() => {
      needReconnection = false
      if (ws) ws.close()
    })

    const messages = computed(() => state.chat.messages)
    const chatMutedAt = computed(() => state.chat.chatMutedAt)
    const countMessages = computed(() => state.chat.countMessages)

    const scrollToDownRef = ref(null)
    // const isOpenNotification = ref(false)
    const isOpenSearch = ref(false)
    // const showTooltip = ref(false)
    const searchValue = ref('')
    const inputSearchRef = ref(null)
    const previousScrollTop = ref(0)
    const isFetchingMessagesAllowed = ref(true)

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const showSidebar = computed(() => state.settings.showSidebar)
    let page = 1

    watch(() => props.showChat, (value) => {
      setTimeout(() => {
        if (value) {
          dispatch('updateMessageStatus', { chatId: book.value.chat.id })
          if (!route.query.message) scrollToElement()
        }
      }, 300)
    })

    onMounted(() => {
      setTimeout(() => {
        if (route.query.message) {
          scrollToMessage()
        }
      }, 300)
    })

    function scrollToMessage () {
      const msg = messageRefs.value[route.query.message]

      if (msg) {
        setTimeout(() => {
          msg.$el.scrollIntoView({ block: 'end', behavior: 'smooth' })
        }, 300)
      } else {
        dispatch('fetchMessages', {
          chatId: book.value.chat.id,
          bookId: book.value.id,
          page: page
        }).then(() => {
          page++
          scrollToMessage()
        })
      }
    }

    function scrollToElement () {
      nextTick(() => {
        if (scrollToDownRef.value) {
          scrollToDownRef.value.scrollTop = scrollToDownRef.value.scrollHeight
        }
      })
    }

    const filteredMessages = computed(() => {
      const result = messages.value.filter((item) => item.text.toLowerCase().includes(searchValue.value.toLowerCase()))
      if (result.length === 0) {
        return [{
          createdAt: new Date(),
          userId: user.value.id,
          text: '<p>Welcome to the Chat! You know how this works 😀</p>',
          user: {
            profile: {
              firstName: 'Eve from',
              lastName: 'hello ivy',
              color: user.value.profile.color
            }
          }
        }]
      }
      return result
    })

    function closeChat () {
      emit('closeChat')
    }

    function pushMessage ({ text, existingImages, deletedImages }) {
      const randomId = Math.random()

      const message = {
        chatId: book.value.chat.id,
        createdAt: new Date(),
        id: randomId,
        messageStatus: {
          status: 'read'
        },
        text: text,
        updatedAt: new Date(),
        user: user.value,
        userId: user.value.id,
        wasSent: false
      }

      commit('setMessage', message)

      dispatch('addMessage', {
        chatId: book.value.chat.id,
        bookId: book.value.id,
        text: text,
        projectId: 'true'
      }).then(res => {
        if (existingImages.length > 0) {
          chatService.updateAttachments({
            messageId: res.data.id,
            bookId: book.value.id,
            ids: existingImages
          })
        }
        if (deletedImages.length > 0) {
          chatService.deleteAttachments(deletedImages)
        }

        chatEditorRef.value.mentionMember()

        commit('updateMessageSendStatus', {
          randomId: randomId,
          id: res.data.id
        })
      })
      scrollToElement()
    }
    function openSearch () {
      isOpenSearch.value = !isOpenSearch.value
    }

    function toggleInput () {
      inputSearchRef.value.focus()
    }

    function scrollListener (e) {
      const top = e.target.scrollTop < 0 ? e.target.scrollHeight + e.target.scrollTop - e.target.clientHeight : e.target.scrollTop

      if (messages.value.length >= countMessages.value) {
        isFetchingMessagesAllowed.value = false
      }

      if (isFetchingMessagesAllowed.value && previousScrollTop.value > top && top < 500 && top >= 100) {
        isFetchingMessagesAllowed.value = false

        dispatch('fetchMessages', {
          chatId: book.value.chat.id,
          bookId: book.value.id,
          page: page
        }).then(() => {
          page++
          setTimeout(() => {
            isFetchingMessagesAllowed.value = true
          }, 1000)
        })
      }

      previousScrollTop.value = top
    }

    function updateMutedAt (value) {
      const date = value ? value.toString() : value

      chatService.updateMutedAt({
        chatId: book.value.chat.id,
        mutedAt: value
      }).then(() => commit('updateChatMutedAt', date))
    }

    return {
      // ref
      messages,
      chatMutedAt,
      scrollToDownRef,
      chatEditorRef,
      messageRefs,
      inputSearchRef,
      // data,
      isOpenSearch,
      searchValue,
      // computed
      isMobile,
      isDarkMode,
      book,
      showSidebar,
      user,
      shareWith,
      bookMembers,
      currentBook,
      highlightedMessage,
      // function
      closeChat,
      pushMessage,
      scrollToElement,
      openSearch,
      filteredMessages,
      toggleInput,
      scrollListener,
      updateMutedAt
    }
  }
})
</script>
